import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { AlertCircle, CheckCircle, CheckDone01, Eye, Settings01, LayersThree01 } from "@untitledui/icons";
import { ActivityFeed } from "@/components/application/activity-feed/activity-feed";
import { ExpandableActivityCard } from "@/components/application/containers/timeline/expandable-activity-card";
import { ActivityFiltersBar } from "@/components/application/activity-feed/activity-filters";
import { TradeCommsView } from "@/components/application/trade-comms";
import { Timeline } from "@/components/application/timeline";
import type { TimelineEntry } from "@/components/application/timeline";
import { useInfiniteScroll } from "@/components/application/containers/use-infinite-scroll";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { ButtonGroup, ButtonGroupItem } from "@/components/base/button-group/button-group";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import {
    useActivities,
    useActivitiesByCategory,
    useActivityUnreadCount,
    useMarkActivityAsRead,
    useMarkAllActivitiesAsRead,
} from "@/hooks/api/use-activities";
import { useAttentionRequired } from "@/hooks/api/use-attention-required";
import { useEmailCounts } from "@/hooks/api/use-email-accounts";
import { mapActivitiesToFrontend } from "@/utils/activity-mapper";
import { deduplicateBackendActivities, deduplicateActivityItems } from "@/utils/deduplicate-activities";
import type { ActivityFilters, ActivityItem } from "@/types/activity";
import type { BackendActivity } from "@/data/api/activities";
import { useTranslation } from "react-i18next";

const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === "true";
const DEPTH_DEFAULT = 50;
const DEPTH_MAX = 200;
const DEPTH_STEP = 50;

type ActivitySection = "trade-comms" | "system-logs";
type SystemLogsView = "carousel" | "timeline" | "focus";

function getSectionFromParams(searchParams: URLSearchParams): ActivitySection {
    const tab = searchParams.get("tab");
    // Legacy "focus" tab → redirect to system-logs
    return tab === "system-logs" || tab === "trade-comms" ? tab : tab === "focus" ? "system-logs" : "trade-comms";
}

function getViewFromParams(searchParams: URLSearchParams): SystemLogsView {
    const view = searchParams.get("view");
    return view === "timeline" || view === "carousel" || view === "focus" ? view : "carousel";
}

function getDepthFromParams(searchParams: URLSearchParams): number {
    const d = searchParams.get("depth");
    if (d == null) return DEPTH_DEFAULT;
    const n = parseInt(d, 10);
    if (Number.isNaN(n)) return DEPTH_DEFAULT;
    return Math.min(DEPTH_MAX, Math.max(DEPTH_DEFAULT, n));
}

export const ShipperActivityPage = () => {
    const { t } = useTranslation(["pages:activity", "common"]);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const emailSettingsPath = `${location.pathname.replace(/\/activity\/?$/, "/settings")}?tab=integrations`;
    const [filters, setFilters] = useState<ActivityFilters>({ readState: "all" });
    const [showAllOccurrences, setShowAllOccurrences] = useState(false);
    // Derive section & view directly from URL — single source of truth
    const section: ActivitySection = getSectionFromParams(searchParams);
    const view: SystemLogsView = getViewFromParams(searchParams);
    const depth = getDepthFromParams(searchParams);

    useEffect(() => {
        const categoryParam = searchParams.get("category");
        const containerParam = searchParams.get("container");
        if (categoryParam || containerParam) {
            setFilters((prev) => ({
                ...prev,
                ...(categoryParam && { activityCategories: [categoryParam] }),
                ...(containerParam && { keyword: containerParam }),
            }));
        }
    }, [searchParams]);

    const apiFiltersForTimeline = useMemo(
        () => ({
            is_read:
                filters.readState === "all" ? undefined : filters.readState === "read",
            from_date: filters.fromDate?.toISOString(),
            to_date: filters.toDate?.toISOString(),
            keyword: filters.keyword,
            limit: depth,
            skip: 0,
        }),
        [filters.readState, filters.fromDate, filters.toDate, filters.keyword, depth]
    );

    const {
        items: systemActivitiesFromApi,
        rawActivitiesMap,
        isLoading: isLoadingCarousel,
        isError: isErrorCarousel,
        error: errorCarousel,
        hasMoreForCategory,
        onLoadMoreForCategory,
        isLoadingMoreForCategory,
    } = useActivitiesByCategory(
        "shipper",
        {
            readState: filters.readState,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            keyword: filters.keyword,
        },
        {
            enabled: !USE_MOCK_DATA && section === "system-logs" && view === "carousel",
            refetchInterval: 30000,
        }
    );

    const {
        data: timelineApiResponse,
        isLoading: isLoadingTimeline,
        isError: isErrorTimeline,
        error: errorTimeline,
    } = useActivities("shipper", apiFiltersForTimeline, {
        enabled: !USE_MOCK_DATA && section === "system-logs" && view === "timeline",
        refetchInterval: 30000,
    });

    const {
        data: attentionData,
        isLoading: isLoadingFocus,
    } = useAttentionRequired(
        200,
        filters.fromDate?.toISOString().slice(0, 10) ?? null,
        filters.toDate?.toISOString().slice(0, 10) ?? null,
        section === 'system-logs',
    );

    const systemActivitiesForCarousel = useMemo(() => {
        if (USE_MOCK_DATA) {
            const { activityService } = require("@/data/mock-activities");
            const activities = activityService.getByAudience("shipper");
            return activities.filter((a: ActivityItem) => a.activityCategory !== "email");
        }
        if (showAllOccurrences) return systemActivitiesFromApi;
        // Deduplicate carousel items
        return deduplicateActivityItems(systemActivitiesFromApi, rawActivitiesMap).map(d => d.item);
    }, [USE_MOCK_DATA, systemActivitiesFromApi, rawActivitiesMap, showAllOccurrences]);

    const systemActivitiesForTimeline = useMemo(() => {
        if (!timelineApiResponse?.activities) return [];
        const mapped = mapActivitiesToFrontend(timelineApiResponse.activities);
        return mapped.filter((a: ActivityItem) => a.activityCategory !== "email");
    }, [timelineApiResponse?.activities]);

    const timelineRawMap = useMemo(() => {
        const map: Record<string, BackendActivity> = {};
        if (timelineApiResponse?.activities) {
            for (const raw of timelineApiResponse.activities) {
                map[raw.activity_id] = raw;
            }
        }
        return map;
    }, [timelineApiResponse?.activities]);

    const systemActivities =
        view === "timeline" ? systemActivitiesForTimeline : systemActivitiesForCarousel;

    const focusActivities = useMemo(() => {
        if (!attentionData?.activities) return { actionNeeded: [] as BackendActivity[], requiresReview: [] as BackendActivity[], totalBeforeDedup: 0 };
        // Apply client-side filters (keyword, readState, categories)
        let filtered = attentionData.activities;
        if (filters.keyword) {
            const kw = filters.keyword.toLowerCase();
            filtered = filtered.filter((a) =>
                (a.title?.toLowerCase().includes(kw)) ||
                (a.summary?.toLowerCase().includes(kw)) ||
                (a.container_number?.toLowerCase().includes(kw))
            );
        }
        if (filters.readState === 'read') {
            filtered = filtered.filter((a) => a.is_read);
        } else if (filters.readState === 'unread') {
            filtered = filtered.filter((a) => !a.is_read);
        }
        if (filters.activityCategories?.length) {
            filtered = filtered.filter((a) => filters.activityCategories!.includes(a.activity_category));
        }
        const actionNeededRaw: BackendActivity[] = [];
        const requiresReviewRaw: BackendActivity[] = [];
        for (const activity of filtered) {
            if (activity.entity_type === 'container_issue' && activity.requires_attention) {
                actionNeededRaw.push(activity);
            } else if (activity.entity_type !== 'container_issue' && activity.requires_attention && !activity.is_read) {
                requiresReviewRaw.push(activity);
            }
        }
        const totalBeforeDedup = actionNeededRaw.length + requiresReviewRaw.length;
        if (showAllOccurrences) {
            return { actionNeeded: actionNeededRaw, requiresReview: requiresReviewRaw, totalBeforeDedup };
        }
        // Deduplicate: keep only latest per (activity_type + expedition_id)
        const dedupAction = deduplicateBackendActivities(actionNeededRaw);
        const dedupReview = deduplicateBackendActivities(requiresReviewRaw);
        return {
            actionNeeded: dedupAction.map(d => d.item),
            requiresReview: dedupReview.map(d => d.item),
            totalBeforeDedup,
        };
    }, [attentionData?.activities, filters.keyword, filters.readState, filters.activityCategories, showAllOccurrences]);

    const { mutate: markAsRead } = useMarkActivityAsRead();
    const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllActivitiesAsRead("shipper");
    const { data: totalUnreadCount = 0 } = useActivityUnreadCount("shipper", {
        enabled: !USE_MOCK_DATA,
        refetchInterval: 60000,
    });

    const handleMarkAsRead = useCallback(
        (activityId: string) => {
            if (!USE_MOCK_DATA) markAsRead({ activityId });
        },
        [markAsRead]
    );

    const timelineEntries = useMemo((): TimelineEntry[] => {
        const sorted = [...systemActivitiesForTimeline].sort(
            (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
        );
        // Deduplicate unless showAllOccurrences is on
        const items = showAllOccurrences
            ? sorted
            : deduplicateActivityItems(sorted, timelineRawMap).map(d => d.item);
        let currentPosition: "left" | "right" = "left";
        let lastCategory: string | undefined;
        return items.map((item) => {
            const category = item.activityCategory ?? "other";
            if (lastCategory !== undefined && category !== lastCategory) {
                currentPosition = currentPosition === "left" ? "right" : "left";
            }
            lastCategory = category;
            return {
                id: item.id,
                date: item.timestamp,
                content: (() => {
                    const raw = timelineRawMap[item.id];
                    if (raw) return <ExpandableActivityCard activity={raw} isContainerContext={false} />;
                    return null;
                })(),
                position: currentPosition,
            };
        });
    }, [systemActivitiesForTimeline, timelineRawMap, handleMarkAsRead, showAllOccurrences]);

    const hasMoreTimeline =
        (timelineApiResponse?.total ?? 0) > systemActivitiesForTimeline.length && depth < DEPTH_MAX;
    const loadMoreTimeline = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                const current = getDepthFromParams(next);
                next.set("depth", String(Math.min(DEPTH_MAX, current + DEPTH_STEP)));
                return next;
            },
            { replace: true }
        );
    }, [setSearchParams]);
    const { loadMoreRef } = useInfiniteScroll({
        hasMore: hasMoreTimeline,
        loadMore: loadMoreTimeline,
        threshold: 200,
    });

    const { data: emailCounts } = useEmailCounts({
        enabled: !USE_MOCK_DATA,
        refetchInterval: 60000,
    });
    const tradeCommsBadgeCount = emailCounts?.inbox_unread_total ?? 0;
    const unreadSystemCount = useMemo(
        () => systemActivities.filter((a: ActivityItem) => !a.isRead).length,
        [systemActivities]
    );

    const handleMarkAllAsRead = () => {
        if (!USE_MOCK_DATA && totalUnreadCount > 0) {
            markAllAsRead();
        }
    };

    const displayTotal = section === "system-logs"
        ? (view === "focus"
            ? focusActivities.actionNeeded.length + focusActivities.requiresReview.length
            : systemActivities.length)
        : 0;

    const isLoadingSystemLogs =
        (view === "carousel" && isLoadingCarousel) || (view === "timeline" && isLoadingTimeline) || (view === "focus" && isLoadingFocus);
    const isErrorSystemLogs =
        (view === "carousel" && isErrorCarousel) || (view === "timeline" && isErrorTimeline);
    const errorSystemLogs = view === "carousel" ? errorCarousel : errorTimeline;

    if (isLoadingSystemLogs && !USE_MOCK_DATA && section === "system-logs") {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t("shipper.title")}</h1>
                    <p className="text-tertiary mt-1">{t("shipper.subtitle")}</p>
                </div>
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-secondary" />
                </div>
            </div>
        );
    }

    if (isErrorSystemLogs && !USE_MOCK_DATA && section === "system-logs") {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t("shipper.title")}</h1>
                    <p className="text-tertiary mt-1">{t("shipper.subtitle")}</p>
                </div>
                <div className="rounded-xl bg-error-50 dark:bg-error-950 p-6 text-error-600 dark:text-error-400">
                    {t("common:error.loadingFailed", { defaultValue: "Errore nel caricamento delle attività" })}
                    {errorSystemLogs instanceof Error && <p className="mt-2 text-sm opacity-75">{errorSystemLogs.message}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t("shipper.title")}</h1>
                    <p className="text-tertiary mt-1">{t("shipper.subtitle")}</p>
                </div>
                <div className="flex items-center gap-4">
                    {!USE_MOCK_DATA && section === "system-logs" && (
                        <Button
                            color={showAllOccurrences ? "primary" : "tertiary"}
                            size="sm"
                            iconLeading={LayersThree01}
                            onClick={() => setShowAllOccurrences(v => !v)}
                        >
                            {showAllOccurrences ? 'Deduplica' : 'Mostra tutto'}
                        </Button>
                    )}
                    {!USE_MOCK_DATA && totalUnreadCount > 0 && (
                        <Button color="tertiary" size="sm" iconLeading={CheckDone01} onClick={handleMarkAllAsRead} isLoading={isMarkingAll}>
                            {t("actions.markAllAsRead")}
                        </Button>
                    )}
                    {!USE_MOCK_DATA && section === "system-logs" && (
                        <div className="text-sm text-tertiary">
                            {displayTotal} {t("common:activities", { defaultValue: "attività" })}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-between gap-4">
                <ButtonGroup
                    selectedKeys={new Set([section])}
                    onSelectionChange={(keys) => {
                        const k = Array.from(keys as Set<string>)[0];
                        if (k === "trade-comms" || k === "system-logs") {
                            setSearchParams((prev) => {
                                const next = new URLSearchParams(prev);
                                next.set("tab", k);
                                return next;
                            }, { replace: true });
                        }
                    }}
                >
                    <ButtonGroupItem id="trade-comms">
                        {t("tradeComms.title", { defaultValue: "Trade Communications" })}
                        {tradeCommsBadgeCount > 0 && (
                            <Badge type="pill-color" size="sm" color="brand" className="ml-1">
                                {tradeCommsBadgeCount}
                            </Badge>
                        )}
                    </ButtonGroupItem>
                    <ButtonGroupItem id="system-logs">
                        {t("systemLogs.title", { defaultValue: "System Activity Logs" })}
                        {unreadSystemCount > 0 && (
                            <Badge type="pill-color" size="sm" color="gray" className="ml-1">
                                {unreadSystemCount}
                            </Badge>
                        )}
                    </ButtonGroupItem>
                </ButtonGroup>
                {section === "trade-comms" && (
                    <Tooltip title={t("tradeComms.goToEmailSettings", { defaultValue: "Go to Email Settings" })} placement="bottom">
                        <Link
                            to={emailSettingsPath}
                            className="p-2 rounded-lg text-tertiary hover:text-primary hover:bg-secondary/50 transition-colors"
                            aria-label={t("tradeComms.goToEmailSettings", { defaultValue: "Go to Email Settings" })}
                        >
                            <Settings01 className="h-5 w-5" />
                        </Link>
                    </Tooltip>
                )}
            </div>

            {section === "trade-comms" ? (
                <TradeCommsView audience="shipper" onMarkActivityAsRead={handleMarkAsRead} />
            ) : (
                <>
                    <ActivityFiltersBar value={filters} onChange={setFilters} />
                    <ButtonGroup
                        selectedKeys={new Set([view])}
                        onSelectionChange={(keys) => {
                            const k = Array.from(keys as Set<string>)[0];
                            if (k === "carousel" || k === "timeline" || k === "focus") {
                                setSearchParams((prev) => {
                                    const next = new URLSearchParams(prev);
                                    next.set("view", k);
                                    if (k === "timeline" && !next.has("depth")) next.set("depth", String(DEPTH_DEFAULT));
                                    return next;
                                }, { replace: true });
                            }
                        }}
                    >
                        <ButtonGroupItem id="carousel">
                            {t("systemLogs.viewCarousel", { defaultValue: "Carousel" })}
                        </ButtonGroupItem>
                        <ButtonGroupItem id="timeline">
                            {t("systemLogs.viewTimeline", { defaultValue: "Timeline" })}
                        </ButtonGroupItem>
                        <ButtonGroupItem id="focus">
                            Focus
                            {(focusActivities.actionNeeded.length + focusActivities.requiresReview.length) > 0 && (
                                <Badge type="pill-color" size="sm" color="warning" className="ml-1">
                                    {focusActivities.actionNeeded.length + focusActivities.requiresReview.length}
                                </Badge>
                            )}
                        </ButtonGroupItem>
                    </ButtonGroup>
                    {view === "focus" ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left: Action needed (issues) */}
                            <div className="flex flex-col">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertCircle className="h-4 w-4 text-warning-600" />
                                    <h3 className="text-sm font-semibold text-primary">Action Needed</h3>
                                    {focusActivities.actionNeeded.length > 0 && (
                                        <Badge color="warning" size="sm">{focusActivities.actionNeeded.length}</Badge>
                                    )}
                                </div>
                                {focusActivities.actionNeeded.length === 0 ? (
                                    <div className="flex flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed border-secondary py-10">
                                        <CheckCircle className="h-8 w-8 text-success-400 mb-2" />
                                        <p className="text-sm text-tertiary">No actions needed</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {focusActivities.actionNeeded.map((activity) => (
                                            <div key={activity.activity_id} className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                                                <ExpandableActivityCard activity={activity} isContainerContext={false} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Right: Requires review */}
                            <div className="flex flex-col">
                                <div className="flex items-center gap-2 mb-3">
                                    <Eye className="h-4 w-4 text-brand-600" />
                                    <h3 className="text-sm font-semibold text-primary">Requires Review</h3>
                                    {focusActivities.requiresReview.length > 0 && (
                                        <Badge color="blue" size="sm">{focusActivities.requiresReview.length}</Badge>
                                    )}
                                </div>
                                {focusActivities.requiresReview.length === 0 ? (
                                    <div className="flex flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed border-secondary py-10">
                                        <CheckCircle className="h-8 w-8 text-success-400 mb-2" />
                                        <p className="text-sm text-tertiary">All caught up</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {focusActivities.requiresReview.map((activity) => (
                                            <div key={activity.activity_id} className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                                                <ExpandableActivityCard activity={activity} isContainerContext={false} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : view === "carousel" ? (
                        <ActivityFeed
                            items={systemActivities}
                            filters={filters}
                            renderItem={(item) => {
                                const raw = rawActivitiesMap[item.id];
                                if (raw) return <ExpandableActivityCard activity={raw} isContainerContext={false} />;
                                return null;
                            }}
                            onLoadMoreForCategory={onLoadMoreForCategory}
                            hasMoreForCategory={hasMoreForCategory}
                            isLoadingMoreForCategory={isLoadingMoreForCategory}
                        />
                    ) : (
                        <div className="rounded-xl bg-primary p-0 shadow-xs ring-1 ring-secondary">
                            <div className="px-4 py-4 sm:px-6">
                                <Timeline items={timelineEntries} order="desc" />
                                <div ref={loadMoreRef} className="h-4 w-4" aria-hidden />
                                {timelineEntries.length > 0 && hasMoreTimeline && isLoadingTimeline && (
                                    <div className="flex justify-center py-4">
                                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-secondary border-t-brand-solid" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ShipperActivityPage;
